import { DateTime } from 'luxon';
import type { ContentType } from './constants.js';

/**
 * Timezone-aware scheduling engine.
 *
 * The same code runs on the server (to show "what should be playing now" in the dashboard)
 * and inside the player (to decide what IS playing, entirely offline). It must therefore stay
 * free of I/O and of any platform-specific import.
 *
 * All evaluation happens in the *location's* IANA timezone, never the server's, and Luxon
 * handles daylight-saving transitions.
 */

export interface TimeRange {
  /** "HH:mm" inclusive start, local to the schedule's timezone. */
  start: string;
  /** "HH:mm" exclusive end. A value <= start means the range crosses midnight. */
  end: string;
}

export interface EvaluableSchedule {
  id: string;
  /** Higher wins. */
  priority: number;
  /** An always-on assignment (no date/time restriction). */
  always: boolean;
  contentType: ContentType;
  contentId: string;
  /** ISO date "YYYY-MM-DD" in the location timezone, inclusive. */
  startDate?: string | null;
  /** ISO date "YYYY-MM-DD" in the location timezone, inclusive. */
  endDate?: string | null;
  /** ISO weekday numbers 1 (Mon) .. 7 (Sun). Empty/undefined = every day. */
  daysOfWeek?: readonly number[] | null;
  /** Empty/undefined = all day. */
  timeRanges?: readonly TimeRange[] | null;
  /** Used as a deterministic tiebreaker; ISO timestamp. */
  updatedAt: string;
  /** Optional display name, handy for debugging and the dashboard. */
  name?: string;
}

export interface ScheduleDecision {
  /** The winning schedule, or null when nothing is scheduled right now. */
  active: EvaluableSchedule | null;
  /** All schedules that match right now, ordered by the same rules as `active`. */
  candidates: EvaluableSchedule[];
  /**
   * What the screen plays now: every matching schedule at the highest priority, in order. They
   * take turns, one's content after the other's, in a loop; a higher priority (a time slot, an
   * urgent message) replaces them all while it applies.
   */
  playing: EvaluableSchedule[];
  /**
   * The next instant (UTC ISO) at which the decision could change, capped to `horizonHours`.
   * The player uses it to wake up exactly on boundaries instead of polling aggressively.
   */
  nextBoundary: string | null;
}

const MINUTES_PER_DAY = 24 * 60;

export function parseHHmm(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isInteger(h) || !Number.isInteger(min)) return null;
  if (h < 0 || h > 24 || min < 0 || min > 59) return null;
  const total = h * 60 + min;
  if (total > MINUTES_PER_DAY) return null;
  return total;
}

export function formatHHmm(minutes: number): string {
  const m = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** A range normalised to minute offsets, flagged when it wraps past midnight. */
interface NormalRange {
  start: number;
  end: number;
  crossesMidnight: boolean;
}

function normalizeRanges(ranges: readonly TimeRange[] | null | undefined): NormalRange[] {
  if (!ranges || ranges.length === 0) return [];
  const out: NormalRange[] = [];
  for (const r of ranges) {
    const start = parseHHmm(r.start);
    const end = parseHHmm(r.end);
    if (start === null || end === null) continue;
    // "00:00" as an end means end-of-day.
    const realEnd = end === 0 ? MINUTES_PER_DAY : end;
    out.push({ start, end: realEnd, crossesMidnight: realEnd <= start });
  }
  return out;
}

function dateWithinBounds(
  localDate: string,
  startDate?: string | null,
  endDate?: string | null,
): boolean {
  if (startDate && localDate < startDate) return false;
  if (endDate && localDate > endDate) return false;
  return true;
}

function dayAllowed(weekday: number, daysOfWeek?: readonly number[] | null): boolean {
  if (!daysOfWeek || daysOfWeek.length === 0) return true;
  return daysOfWeek.includes(weekday);
}

/**
 * Does the schedule match at this local instant?
 *
 * A time range that crosses midnight (22:00 → 02:00) belongs to the day it *starts* on:
 * the 00:00–02:00 tail is matched against the previous day's date and weekday rules.
 */
export function scheduleMatchesAt(schedule: EvaluableSchedule, local: DateTime): boolean {
  if (schedule.always) return true;

  const ranges = normalizeRanges(schedule.timeRanges);
  const minutes = local.hour * 60 + local.minute;
  const today = local.toFormat('yyyy-MM-dd');

  if (ranges.length === 0) {
    return (
      dateWithinBounds(today, schedule.startDate, schedule.endDate) &&
      dayAllowed(local.weekday, schedule.daysOfWeek)
    );
  }

  for (const range of ranges) {
    if (!range.crossesMidnight) {
      if (minutes >= range.start && minutes < range.end) {
        if (
          dateWithinBounds(today, schedule.startDate, schedule.endDate) &&
          dayAllowed(local.weekday, schedule.daysOfWeek)
        ) {
          return true;
        }
      }
      continue;
    }

    // Crosses midnight: evening portion belongs to today...
    if (minutes >= range.start) {
      if (
        dateWithinBounds(today, schedule.startDate, schedule.endDate) &&
        dayAllowed(local.weekday, schedule.daysOfWeek)
      ) {
        return true;
      }
    }
    // ...and the early-morning portion belongs to yesterday's occurrence.
    if (minutes < range.end) {
      const yesterday = local.minus({ days: 1 });
      if (
        dateWithinBounds(
          yesterday.toFormat('yyyy-MM-dd'),
          schedule.startDate,
          schedule.endDate,
        ) &&
        dayAllowed(yesterday.weekday, schedule.daysOfWeek)
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Deterministic ordering: priority desc, then dated campaigns before always-on content at the
 * same priority, then most recently updated, then id for a total order.
 */
export function compareSchedules(a: EvaluableSchedule, b: EvaluableSchedule): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  if (a.always !== b.always) return a.always ? 1 : -1;
  if (a.updatedAt !== b.updatedAt) return a.updatedAt < b.updatedAt ? 1 : -1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Find the next instant at which any schedule's match state could flip.
 * Scans minute boundaries derived from the schedules themselves (range starts/ends and
 * midnight) rather than brute-forcing every minute.
 */
function computeNextBoundary(
  schedules: readonly EvaluableSchedule[],
  now: DateTime,
  timezone: string,
  horizonHours: number,
): DateTime | null {
  const limit = now.plus({ hours: horizonHours });
  const candidates: DateTime[] = [];

  const dayStarts: DateTime[] = [];
  let cursor = now.setZone(timezone).startOf('day');
  while (cursor <= limit) {
    dayStarts.push(cursor);
    cursor = cursor.plus({ days: 1 });
    // Guard against pathological zone math.
    if (dayStarts.length > horizonHours / 24 + 3) break;
  }

  for (const day of dayStarts) {
    candidates.push(day); // midnight: date/weekday rules change
    for (const s of schedules) {
      if (s.always) continue;
      const ranges = normalizeRanges(s.timeRanges);
      if (ranges.length === 0) continue;
      for (const r of ranges) {
        candidates.push(day.plus({ minutes: r.start }));
        candidates.push(day.plus({ minutes: r.end }));
      }
    }
  }

  let best: DateTime | null = null;
  for (const c of candidates) {
    if (!c.isValid) continue;
    if (c <= now) continue;
    if (c > limit) continue;
    if (!best || c < best) best = c;
  }
  return best;
}

export interface EvaluateOptions {
  /** IANA timezone of the screen's location, e.g. "Asia/Amman". */
  timezone: string;
  /** Instant to evaluate; defaults to now. */
  now?: Date;
  /** How far ahead to look for the next boundary. Default 24h. */
  horizonHours?: number;
}

export function evaluateSchedules(
  schedules: readonly EvaluableSchedule[],
  options: EvaluateOptions,
): ScheduleDecision {
  const zone = options.timezone && DateTime.local().setZone(options.timezone).isValid
    ? options.timezone
    : 'UTC';
  const nowUtc = DateTime.fromJSDate(options.now ?? new Date(), { zone: 'utc' });
  const local = nowUtc.setZone(zone);
  const horizon = options.horizonHours ?? 24;

  const candidates = schedules
    .filter((s) => scheduleMatchesAt(s, local))
    .sort(compareSchedules);

  const boundary = computeNextBoundary(schedules, local, zone, horizon);

  const top = candidates[0];
  return {
    active: top ?? null,
    candidates,
    playing: top ? candidates.filter((s) => s.priority === top.priority) : [],
    nextBoundary: boundary ? boundary.toUTC().toISO() : null,
  };
}

/** Convenience: does this schedule ever produce content again after `now`? */
export function isScheduleExpired(
  schedule: EvaluableSchedule,
  timezone: string,
  now: Date = new Date(),
): boolean {
  if (schedule.always || !schedule.endDate) return false;
  const local = DateTime.fromJSDate(now, { zone: 'utc' }).setZone(timezone);
  return local.toFormat('yyyy-MM-dd') > schedule.endDate;
}

/** Validate a set of time ranges, returning human-readable problems. */
export function validateTimeRanges(ranges: readonly TimeRange[]): string[] {
  const errors: string[] = [];
  ranges.forEach((r, i) => {
    const s = parseHHmm(r.start);
    const e = parseHHmm(r.end);
    if (s === null) errors.push(`Range ${i + 1}: invalid start time "${r.start}"`);
    if (e === null) errors.push(`Range ${i + 1}: invalid end time "${r.end}"`);
    if (s !== null && e !== null && s === (e === 0 ? MINUTES_PER_DAY : e)) {
      errors.push(`Range ${i + 1}: start and end are identical`);
    }
  });
  return errors;
}

/* ------------------------------------------------------------------ timeline */

export interface TimelineSegment {
  /** Minutes from local midnight. */
  from: number;
  to: number;
  /** The winning schedule, or null when nothing plays. */
  scheduleId: string | null;
  /** Every schedule taking turns in this run (same top priority); the first is `scheduleId`. */
  scheduleIds: string[];
}

export interface TimelineDay {
  /** Local date, `yyyy-MM-dd`. */
  date: string;
  /** ISO weekday, 1 = Monday. */
  weekday: number;
  segments: TimelineSegment[];
}

/**
 * What plays when, day by day, in the screen's own timezone: the decision the player makes,
 * sampled every `stepMinutes` and merged into runs. For showing a week at a glance.
 */
export function scheduleTimeline(
  schedules: readonly EvaluableSchedule[],
  options: { timezone: string; days?: number; stepMinutes?: number; start?: Date },
): TimelineDay[] {
  const zone = options.timezone && DateTime.local().setZone(options.timezone).isValid ? options.timezone : 'UTC';
  const step = options.stepMinutes ?? 15;
  const first = DateTime.fromJSDate(options.start ?? new Date(), { zone }).startOf('day');
  const days: TimelineDay[] = [];
  for (let d = 0; d < (options.days ?? 7); d += 1) {
    const day = first.plus({ days: d });
    const segments: TimelineSegment[] = [];
    for (let minute = 0; minute < 24 * 60; minute += step) {
      // Sampled in the middle of the step, so a slot starting on the step boundary counts.
      const at = day.plus({ minutes: minute + step / 2 });
      const decision = evaluateSchedules(schedules, { timezone: zone, now: at.toJSDate(), horizonHours: 0 });
      const scheduleIds = decision.playing.map((s) => s.id);
      const scheduleId = scheduleIds[0] ?? null;
      const last = segments[segments.length - 1];
      if (last && last.scheduleIds.join() === scheduleIds.join()) last.to = minute + step;
      else segments.push({ from: minute, to: minute + step, scheduleId, scheduleIds });
    }
    days.push({ date: day.toFormat('yyyy-MM-dd'), weekday: day.weekday, segments });
  }
  return days;
}
